/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useAppDispatch, useGetUserData, useUserReducer } from "../../Utility";
import "./BusinessUsersPage.css";
import CommonTable from "../Components/CommonTable";
import {
  onGetUsers,
  onPostUsers,
  onPostUsersReset,
} from "../../Store/Slices/userSlice";
import toast from "react-hot-toast";

const PAGE_SIZE = 10;

const ACTION_TOGGLE_ACTIVE = "toggle_active";
const ACTION_DELETE = "delete";

function ManageUsersPage() {
  const dispatch = useAppDispatch();
  const usersRaw = useGetUserData();
  const list = Array.isArray(usersRaw) ? usersRaw : [];
  const filteredList = list.filter(
    (u) =>
      (u.role && (u.role === "user" || u.role === "business")) ||
      (u.user_type && (u.user_type === "user" || u.user_type === "business")),
  );
  const userReducer = useUserReducer();
  const userLoading =
    userReducer?.postUsersLoading || userReducer?.getUsersLoading;
  const [confirmModal, setConfirmModal] = useState(null);

  const openConfirm = (row, action) => {
    const name =
      row.name || row.email || row.business_name || `User #${row.id ?? "?"}`;
    const role =
      row.role === "business" || row.user_type === "business"
        ? "Business"
        : "Normal";
    setConfirmModal({
      id: row.id,
      name,
      role,
      action,
      isActive: row.is_active !== false,
    });
  };

  const closeConfirm = () => {
    if (!userLoading) setConfirmModal(null);
  };

  const handleConfirm = () => {
    if (!confirmModal?.id) return;
    if (confirmModal.action === ACTION_TOGGLE_ACTIVE) {
      dispatch(
        onPostUsers({
          id: confirmModal.id,
          is_active: !confirmModal.isActive,
        }),
      );
    } else if (confirmModal.action === ACTION_DELETE) {
      dispatch(onPostUsers({ id: confirmModal.id, is_deleted: true }));
    }
    setConfirmModal(null);
  };
  useEffect(() => {
    if (userReducer?.postUsersStatusCode === "200") {
      dispatch(onGetUsers());
      dispatch(onPostUsersReset());
      toast.success(userReducer?.postUsersMessage);
    } else if (userReducer?.postUsersStatusCode === "400") {
      dispatch(onPostUsersReset());
      toast.error(userReducer?.postUsersMessage);
    } else if (userReducer?.postUsersStatusCode) {
      dispatch(onPostUsersReset());
      toast.error(userReducer?.postUsersMessage);
    }
  }, [userReducer?.postUsersStatusCode]);
  const columns = [
    {
      key: "id",
      title: "ID",
      dataIndex: "id",
      render: (row) => row.id ?? "—",
    },
    {
      key: "name",
      title: "Name",
      render: (row) => (row.name || row.business_name) ?? "—",
    },
    {
      key: "email",
      title: "Email",
      dataIndex: "email",
      render: (row) => row.email ?? "—",
    },
    {
      key: "phone",
      title: "Phone Number",
      dataIndex: "phone",
      render: (row) => row.phone ?? "—",
    },
    {
      key: "role",
      title: "Type",
      render: (row) => {
        const r = row.role || row.user_type || "";
        if (r === "business") return "Business";
        if (r === "user") return "Normal";
        return r ? String(r).charAt(0).toUpperCase() + String(r).slice(1) : "—";
      },
    },
    {
      key: "is_active",
      title: "Status",
      render: (row) => (
        <span
          className={`admin-table-status admin-table-status-${row.is_active !== false ? "approved" : "rejected"}`}
        >
          {row.is_active !== false ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "created_at",
      title: "Registered",
      render: (row) =>
        row.created_at
          ? new Date(row.created_at).toLocaleDateString("en-IN", {
              dateStyle: "short",
            })
          : "—",
    },
    {
      key: "actions",
      title: "Actions",
      render: (row) => (
        <div
          className="admin-table-actions"
          style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
        >
          <button
            type="button"
            className={
              row.is_active !== false
                ? "admin-btn-deactivate"
                : "admin-btn-approve"
            }
            onClick={() => openConfirm(row, ACTION_TOGGLE_ACTIVE)}
            disabled={userLoading}
          >
            {row.is_active !== false ? "Deactivate" : "Activate"}
          </button>
          <button
            type="button"
            className="admin-btn-delete"
            onClick={() => openConfirm(row, ACTION_DELETE)}
            disabled={userLoading}
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  const loading = userLoading;

  return (
    <div className="admin-business-users">
      <div className="admin-page-header" style={{ marginBottom: "1rem" }}>
        <h1 className="admin-page-title">Manage Users</h1>
        <p className="admin-page-subtitle">
          Business and normal users only. Toggle active status or delete.
        </p>
      </div>
      <CommonTable
        columns={columns}
        data={filteredList}
        loading={loading}
        emptyText="No users found"
        showSearch
        searchPlaceholder="Search all..."
        searchKeys={["id", "name", "business_name", "email"]}
        pageSize={PAGE_SIZE}
      />

      {confirmModal && (
        <div
          className="admin-modal-overlay"
          onClick={closeConfirm}
          role="dialog"
          aria-modal="true"
          aria-labelledby="manage-user-modal-title"
        >
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h2 id="manage-user-modal-title" className="admin-modal-title">
              {confirmModal.action === ACTION_DELETE
                ? "Delete user?"
                : confirmModal.isActive
                  ? "Deactivate user?"
                  : "Activate user?"}
            </h2>
            <p className="admin-modal-text">
              {confirmModal.action === ACTION_DELETE ? (
                <>
                  Are you sure you want to delete{" "}
                  <strong>{confirmModal.name}</strong> ({confirmModal.role})?
                  This action cannot be undone.
                </>
              ) : confirmModal.isActive ? (
                <>
                  Deactivate <strong>{confirmModal.name}</strong>? They will not
                  be able to log in until activated again.
                </>
              ) : (
                <>
                  Activate <strong>{confirmModal.name}</strong>? They will be
                  able to log in again.
                </>
              )}
            </p>
            <div className="admin-modal-actions">
              <button
                type="button"
                className="admin-modal-btn admin-modal-cancel"
                onClick={closeConfirm}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`admin-modal-btn ${confirmModal.action === ACTION_DELETE ? "admin-modal-reject" : "admin-modal-confirm"}`}
                onClick={handleConfirm}
                disabled={loading}
              >
                {loading
                  ? "Please wait…"
                  : confirmModal.action === ACTION_DELETE
                    ? "Delete"
                    : confirmModal.isActive
                      ? "Deactivate"
                      : "Activate"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageUsersPage;
